import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { getKey } from "@redotech/react-util/key";
import { useTriggerLoad } from "@redotech/react-util/load";
import {
  getServiceLabel,
  ServiceSchema,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { RateTableCarrier } from "@redotech/redo-fulfillment-outbound-labels-common/rate-table-carrier";
import { LengthUnit } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import {
  RateProvider,
  rateTableProviderLabel,
} from "@redotech/redo-model/outbound-labels/outbound-labels";
import {
  AxisKind,
  isOutboundRateTable,
  OutboundRateTable,
  OutboundRateTableType,
  RateTable,
  RateTableType,
  RateTableValueMode,
  WeightUnit,
} from "@redotech/redo-model/rate-table/rate-table";
import {
  FedexRateTableServiceLevel,
  rateTableCarrierLabel,
  RateTableServiceLevel,
  RateTableServiceLevelsMapping,
} from "@redotech/redo-model/rate-table/rate-table-mapping";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import CopyIcon from "@redotech/redo-web/icon-old/copy.svg";
import PlusIcon from "@redotech/redo-web/icon-old/plus.svg";
import TrashIcon from "@redotech/redo-web/icon-old/trash.svg";
import { FormTextInput } from "@redotech/redo-web/text-input";
import { memo, useEffect, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../../team";
import { LabelRateTableForm } from "../../common/rate-tables/rate-table";
import {
  getRateTableValueModeLabel,
  rateCardForm,
  RateCardValue,
  rateTableToTableData,
  tableDataToMinMaxCubicSizes,
  tableDataToMinMaxWeights,
  tableDataToRateData,
  tableDataToZones,
} from "../../common/rate-tables/rate-table-utils";
import { defaultNewOutboundRateTable } from "./default-new-rate-table";
import * as styles from "./rate-table-card.module.css";

const RateTableRateProviders: RateProvider[] = [
  RateProvider.EASYPOST,
  RateProvider.UPS,
  RateProvider.FEDEX,
  RateProvider.DHL_ECOMMERCE,
  RateProvider.CANADA_POST,
  RateProvider.REDO_LANDMARK_GLOBAL,
];

const rateTableCarriersToIgnore: RateTableCarrier[] = [
  RateTableCarrier.USPS_RETURNS,
  RateTableCarrier.UPS_SUREPOST,
  RateTableCarrier.DHL_EXPRESS,
  RateTableCarrier.DHL_ECS,
  RateTableCarrier.VEHO,
  RateTableCarrier.FIRST_MILE,
  RateTableCarrier.APC,
  RateTableCarrier.SPEE_DEE,
  RateTableCarrier.PASSPORT_GLOBAL,
  RateTableCarrier.LANDMARK_GLOBAL,
  RateTableCarrier.ONTRAC,
  RateTableCarrier.ROYAL_MAIL_V3,
  RateTableCarrier.ASENDIA,
  RateTableCarrier.GLS,
];

const ProviderToCarriers: Partial<Record<RateProvider, RateTableCarrier[]>> = {
  [RateProvider.EASYPOST]: Object.values(RateTableCarrier).filter(
    (carrier) => !rateTableCarriersToIgnore.includes(carrier),
  ),
  [RateProvider.UPS]: [RateTableCarrier.UPS],
  [RateProvider.FEDEX]: [RateTableCarrier.FEDEX],
  [RateProvider.CANADA_POST]: [RateTableCarrier.CANADA_POST],
  [RateProvider.DHL_ECOMMERCE]: [RateTableCarrier.DHL_ECS],
};

export const OutboundLabelRateCardCard = memo(
  function OutboundLabelRateCardCard({
    readonly = false,
  }: {
    readonly: boolean;
  }) {
    const team = useRequiredContext(TeamContext);
    const client = useRequiredContext(RedoAdminRpcClientContext);
    const [rateCardLoad, triggerRateCardLoad] = useTriggerLoad<
      OutboundRateTable[]
    >(async () => {
      const tables = await client.getTeamRateTables({ teamId: team._id });
      const filtered: OutboundRateTable[] = tables.rateTables.filter(
        (table: RateTable) => isOutboundRateTable(table),
      );
      return filtered;
    });

    const [selectedCarrier, setSelectedCarrier] = useState<RateTableCarrier>(
      RateTableCarrier.USPS,
    );

    const [selectedServiceLevel, setSelectedServiceLevel] =
      useState<RateTableServiceLevel>("Default");

    const [selectedValueMode, setSelectedValueMode] =
      useState<RateTableValueMode>(RateTableValueMode.Spread);

    const [selectedRateTableType, setSelectedRateTableType] =
      useState<OutboundRateTableType>(RateTableType.OUTBOUND_WEIGHT_BY_ZONE);

    const [selectedProvider, setSelectedProvider] = useState<RateProvider>(
      RateProvider.EASYPOST,
    );

    // When provider changes, reset carrier to the first available carrier for that provider
    useEffect(() => {
      const carriers = ProviderToCarriers[selectedProvider];
      if (carriers && !!carriers[0]) {
        setSelectedCarrier(carriers[0]);
      }
    }, [selectedProvider]);

    // Service levels depends on the carrier
    const serviceLevelOptions = useMemo(() => {
      return RateTableServiceLevelsMapping[selectedCarrier];
    }, [selectedCarrier]);

    useEffect(() => {
      // Default service levels should always be spread rate tables
      if (selectedServiceLevel === "Default") {
        setSelectedValueMode(RateTableValueMode.Spread);
        setSelectedRateTableType(RateTableType.OUTBOUND_WEIGHT_BY_ZONE);
      }
    }, [selectedServiceLevel]);

    const valueModeOptions = useMemo(() => {
      const oneRateServiceLevels: RateTableServiceLevel[] = [
        FedexRateTableServiceLevel.OneRateStandard,
        FedexRateTableServiceLevel.OneRatePriorityOvernight,
        FedexRateTableServiceLevel.OneRateFirstOvernight,
        FedexRateTableServiceLevel.OneRateTwoDay,
        FedexRateTableServiceLevel.OneRateTwoDayAM,
        FedexRateTableServiceLevel.OneRateExpressSaver,
      ];

      if (
        oneRateServiceLevels.includes(selectedServiceLevel) ||
        selectedCarrier === RateTableCarrier.UPS ||
        selectedServiceLevel === "Default"
      ) {
        return [RateTableValueMode.Spread, RateTableValueMode.SpreadPercent];
      }
      return [
        RateTableValueMode.Spread,
        RateTableValueMode.SpreadPercent,
        RateTableValueMode.Rate,
      ];
    }, [selectedServiceLevel, selectedCarrier]);

    // Select the first value mode for that carrier when the service level options change
    useEffect(() => {
      if (valueModeOptions[0]) {
        setSelectedValueMode(valueModeOptions[0]);
      }
    }, [valueModeOptions]);

    // Select the first service level for that carrier when the service level options change
    useEffect(() => {
      if (serviceLevelOptions[0]) {
        setSelectedServiceLevel(serviceLevelOptions[0]);
      }
    }, [serviceLevelOptions]);

    // USPS is the only carrier where we differentiate between weight and size rate tables, and we don't do this for default rate tables
    const rateTableTypeOptions = useMemo<OutboundRateTableType[]>(() => {
      if (
        selectedCarrier === RateTableCarrier.USPS &&
        selectedServiceLevel !== "Default"
      ) {
        return [
          RateTableType.OUTBOUND_WEIGHT_BY_ZONE,
          RateTableType.OUTBOUND_SIZE_BY_ZONE,
        ];
      }
      return [RateTableType.OUTBOUND_WEIGHT_BY_ZONE];
    }, [selectedCarrier, selectedServiceLevel]);

    useEffect(() => {
      if (rateTableTypeOptions[0]) {
        setSelectedRateTableType(rateTableTypeOptions[0]);
      }
    }, [rateTableTypeOptions]);

    useEffect(() => {
      triggerRateCardLoad();
    }, [team, client, triggerRateCardLoad]);

    const rateTableInDB = useMemo(() => {
      const rateTable = rateCardLoad.value?.find(
        (rateTable: OutboundRateTable) =>
          rateTable.carrier === selectedCarrier &&
          rateTable.serviceLevel === selectedServiceLevel &&
          rateTable.valueMode === selectedValueMode &&
          rateTable.rateTableType === selectedRateTableType &&
          rateTable.provider === selectedProvider,
      );
      if (!rateTable) {
        return undefined;
      }
      return {
        defaultValue: rateTable.defaultValue,
        defaultValuePercentage:
          rateTable.defaultValuePercentage === null
            ? undefined
            : rateTable.defaultValuePercentage,
        useDefaultValuePercentage:
          rateTable.useDefaultValuePercentage === null
            ? undefined
            : rateTable.useDefaultValuePercentage,
        data: rateTableToTableData(rateTable),
      };
    }, [
      rateCardLoad.value,
      selectedCarrier,
      selectedServiceLevel,
      selectedValueMode,
      selectedRateTableType,
      selectedProvider,
    ]);

    return (
      <OutboundLabelRateCardCardInternal
        key={getKey(rateTableInDB)}
        rateTableInDB={rateTableInDB}
        rateTableTypeOptions={rateTableTypeOptions}
        readonly={readonly}
        selectedCarrier={selectedCarrier}
        selectedProvider={selectedProvider}
        selectedRateTableType={selectedRateTableType}
        selectedServiceLevel={selectedServiceLevel}
        selectedValueMode={selectedValueMode}
        serviceLevelOptions={serviceLevelOptions}
        setSelectedCarrier={setSelectedCarrier}
        setSelectedProvider={setSelectedProvider}
        setSelectedRateTableType={setSelectedRateTableType}
        setSelectedServiceLevel={setSelectedServiceLevel}
        setSelectedValueMode={setSelectedValueMode}
        triggerReload={triggerRateCardLoad}
        valueModeOptions={valueModeOptions}
      />
    );
  },
);

const OutboundLabelRateCardCardInternal = memo(
  function OutboundLabelRateCardCardInternal({
    rateTableInDB,
    triggerReload,
    serviceLevelOptions,
    valueModeOptions,
    rateTableTypeOptions,
    selectedCarrier,
    selectedServiceLevel,
    selectedValueMode,
    selectedRateTableType,
    selectedProvider,
    setSelectedCarrier,
    setSelectedServiceLevel,
    setSelectedValueMode,
    setSelectedRateTableType,
    setSelectedProvider,
    readonly,
  }: {
    rateTableInDB: RateCardValue | undefined;
    triggerReload: () => void;
    serviceLevelOptions: RateTableServiceLevel[];
    valueModeOptions: RateTableValueMode[];
    rateTableTypeOptions: OutboundRateTableType[];
    selectedCarrier: RateTableCarrier;
    selectedServiceLevel: RateTableServiceLevel;
    selectedValueMode: RateTableValueMode;
    selectedRateTableType: OutboundRateTableType;
    selectedProvider: RateProvider;
    setSelectedCarrier: (carrier: RateTableCarrier) => void;
    setSelectedServiceLevel: (serviceLevel: RateTableServiceLevel) => void;
    setSelectedValueMode: (valueMode: RateTableValueMode) => void;
    setSelectedRateTableType: (rateTableType: OutboundRateTableType) => void;
    setSelectedProvider: (provider: RateProvider) => void;
    readonly: boolean;
  }) {
    const client = useRequiredContext(RedoAdminRpcClientContext);
    const team = useRequiredContext(TeamContext);
    const [savePending, setSavePending] = useState(false);

    const initialRateTable =
      rateTableInDB ??
      defaultNewOutboundRateTable(
        selectedCarrier,
        selectedServiceLevel,
        selectedValueMode,
        selectedRateTableType,
        selectedProvider,
      );

    const rateTableDataInput = useInput(rateCardForm, {
      defaultValue: initialRateTable.defaultValue,
      defaultValuePercentage:
        initialRateTable.defaultValuePercentage === null
          ? undefined
          : initialRateTable.defaultValuePercentage,
      useDefaultValuePercentage:
        initialRateTable.useDefaultValuePercentage === null
          ? undefined
          : initialRateTable.useDefaultValuePercentage,
      data: initialRateTable.data,
    });

    const saveDisabled = useMemo(
      () => savePending || rateTableDataInput.allErrors.length > 0 || readonly,
      [savePending, rateTableDataInput.allErrors.length, readonly],
    );

    const saveRateCard = async () => {
      if (readonly) {
        alert("Insufficient permissions to save rate table");
        return;
      }
      const tableData = tableDataToRateData(rateTableDataInput.value.data);
      switch (selectedValueMode) {
        case RateTableValueMode.Spread: {
          const hasLargeValue = tableData.some((row) =>
            row.some((value) => Number(value) > 5),
          );
          if (hasLargeValue) {
            const confirmed = confirm(
              "Spread values should be less than $5. Do you want to continue?",
            );
            if (!confirmed) {
              return;
            }
          }

          break;
        }
        case RateTableValueMode.Rate: {
          const hasSmallValue = tableData.some((row) =>
            row.some((value) => Number(value) < 3.3),
          );
          if (hasSmallValue) {
            const confirmed = confirm(
              "Rate values should be greater than $3.3. Do you want to continue?",
            );
            if (!confirmed) {
              return;
            }
          }

          break;
        }
        case RateTableValueMode.SpreadPercent: {
          const hasLargeValue = tableData.some((row) =>
            row.some((value) => Number(value) > 50),
          );
          if (hasLargeValue) {
            const confirmed = confirm(
              "Spread percent values should be less than 50%. Do you want to continue?",
            );
            if (!confirmed) {
              return;
            }
          }
          const hasNegativeValue = tableData.some((row) =>
            row.some((value) => Number(value) < 0),
          );
          if (hasNegativeValue) {
            const confirmed = confirm(
              "Spread percent values should not be negative. Do you want to continue?",
            );
            if (!confirmed) {
              return;
            }
          }

          break;
        }
        // No default
      }

      setSavePending(true);
      const table: RateTable =
        selectedRateTableType === RateTableType.OUTBOUND_WEIGHT_BY_ZONE
          ? {
              rateTableType: RateTableType.OUTBOUND_WEIGHT_BY_ZONE,
              valueMode: selectedValueMode,
              carrier: selectedCarrier,
              serviceLevel: selectedServiceLevel,
              provider: selectedProvider,
              data: tableData,
              xAxis: {
                values: tableDataToZones(rateTableDataInput.value.data),
                kind: AxisKind.ZONE,
              },
              yAxis: {
                values: tableDataToMinMaxWeights(rateTableDataInput.value.data),
                kind: AxisKind.WEIGHT,
                units: WeightUnit.LB,
              },
              defaultValue: rateTableDataInput.value.defaultValue,
              defaultValuePercentage:
                rateTableDataInput.value.defaultValuePercentage,
              useDefaultValuePercentage:
                rateTableDataInput.value.useDefaultValuePercentage,
            }
          : {
              rateTableType: RateTableType.OUTBOUND_SIZE_BY_ZONE,
              valueMode: selectedValueMode,
              carrier: selectedCarrier,
              serviceLevel: selectedServiceLevel,
              provider: selectedProvider,
              data: tableData,
              xAxis: {
                values: tableDataToZones(rateTableDataInput.value.data),
                kind: AxisKind.ZONE,
              },
              yAxis: {
                values: tableDataToMinMaxCubicSizes(
                  rateTableDataInput.value.data,
                ),
                kind: AxisKind.CUBIC_SIZE,
                units: LengthUnit.INCH,
              },
              defaultValue: rateTableDataInput.value.defaultValue,
              defaultValuePercentage:
                rateTableDataInput.value.defaultValuePercentage,
              useDefaultValuePercentage:
                rateTableDataInput.value.useDefaultValuePercentage,
            };

      try {
        await client.saveTeamRateTable({ rateTable: table, teamId: team._id });
      } catch (error) {
        toast("Failed to save rate table", { variant: "error" });
        console.error(error);
      } finally {
        setSavePending(false);
        triggerReload();
      }
    };

    const deleteRateTable = useHandler(async () => {
      if (readonly) {
        alert("Insufficient permissions to delete rate table");
        return;
      }
      const confirmed = confirm(
        "Are you sure you want to delete this rate table?",
      );
      if (confirmed) {
        await client.deleteRateTable({
          teamId: team._id,
          carrier: selectedCarrier,
          serviceLevel: selectedServiceLevel,
          valueMode: selectedValueMode,
          rateTableType: selectedRateTableType,
          provider: selectedProvider,
        });
        triggerReload();
      }
    });

    const addBottomRow = () => {
      const bottomRow =
        rateTableDataInput.value.data.at(-1)?.map((_) => "0") ?? [];
      rateTableDataInput.inputs.data.setValue([
        ...rateTableDataInput.value.data,
        bottomRow,
      ]);
    };

    const addRightColumn = () => {
      const newColumn =
        rateTableDataInput.value.data
          .map((row) => row.at(-1))
          .map((_) => "0") ?? [];
      const newCharges = rateTableDataInput.value.data.map((row, index) => [
        ...row,
        newColumn.at(index) ?? "0",
      ]);
      rateTableDataInput.inputs.data.setValue(newCharges);
    };

    const deleteRow = (index: number) => {
      rateTableDataInput.inputs.data.setValue(
        rateTableDataInput.value.data.filter((_, i) => i !== index),
      );
    };

    const deleteColumn = (columnIndex: number) => {
      rateTableDataInput.inputs.data.setValue(
        rateTableDataInput.value.data.map((row) =>
          row.filter((_, i) => i !== columnIndex),
        ),
      );
    };

    const [addRateTableLoad, triggerAddRateTable] = useTriggerLoad(async () => {
      await client.saveTeamRateTable({
        rateTable: defaultNewOutboundRateTable(
          selectedCarrier,
          selectedServiceLevel,
          selectedValueMode,
          selectedRateTableType,
          selectedProvider,
        ),
        teamId: team._id,
      });
      triggerReload();
    });

    const copyTable = () => {
      const data = rateTableDataInput.value.data.map((row) => [...row]);
      // Make sure we copy a blank cell for the upper left cell, it's useless
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      data[0][0] = "";
      const excelData = data.map((row) => row.join("\t")).join("\n");
      void navigator.clipboard.writeText(excelData);
    };

    const carrierOptions = (
      ProviderToCarriers[selectedProvider] &&
      ProviderToCarriers[selectedProvider].length > 0
        ? ProviderToCarriers[selectedProvider]
        : Object.values(RateTableCarrier)
    ).map<RedoListItem<RateTableCarrier>>((s) => ({
      id: s,
      type: "text",
      text: rateTableCarrierLabel(s),
      value: s,
    }));

    const getRateTableServiceLevelLabel = (
      serviceLevel: RateTableServiceLevel,
    ) => {
      const parsedServiceLevel = ServiceSchema.safeParse(serviceLevel);
      return parsedServiceLevel.success &&
        parsedServiceLevel.data &&
        parsedServiceLevel.data !== undefined
        ? getServiceLabel(parsedServiceLevel.data)
        : serviceLevel;
    };

    return (
      <Card
        title={`Outbound rate tables — ${getRateTableValueModeLabel(selectedValueMode)}`}
      >
        <Flex dir="row" justify="space-between">
          <Flex dir="row" justify="space-between" w="full">
            <Flex dir="column" w="full">
              <Flex dir="row" justify="space-between" w="full">
                <Flex align="flex-end" dir="row" justify="flex-end">
                  <RedoSingleSelectDropdownInput<RateProvider>
                    className={styles.rateTableCarrierDropdown}
                    label="Rate source"
                    options={RateTableRateProviders.map<
                      RedoListItem<RateProvider>
                    >((s) => ({
                      id: s,
                      type: "text",
                      text: rateTableProviderLabel[s],
                      value: s,
                    }))}
                    optionSelected={({ item }) => {
                      setSelectedProvider(item.value);
                    }}
                    selectedItem={{
                      id: selectedProvider,
                      type: "text",
                      text: rateTableProviderLabel[selectedProvider],
                      value: selectedProvider,
                    }}
                    size="xs"
                  />
                  <RedoSingleSelectDropdownInput<RateTableCarrier>
                    className={styles.rateTableCarrierDropdown}
                    label="Carrier"
                    options={carrierOptions}
                    optionSelected={({ item }) => {
                      setSelectedCarrier(item.value);
                    }}
                    selectedItem={{
                      id: selectedCarrier,
                      type: "text",
                      text: rateTableCarrierLabel(selectedCarrier),
                      value: selectedCarrier,
                    }}
                    size="xs"
                  />
                  <RedoSingleSelectDropdownInput<RateTableServiceLevel>
                    className={styles.rateTableServiceLevelDropdown}
                    label="Service level"
                    options={serviceLevelOptions.map((s) => ({
                      id: s,
                      type: "text",
                      text: getRateTableServiceLevelLabel(s),
                      value: s,
                    }))}
                    optionSelected={({ item }) => {
                      setSelectedServiceLevel(item.value);
                    }}
                    selectedItem={{
                      id: selectedServiceLevel,
                      type: "text",
                      text: getRateTableServiceLevelLabel(selectedServiceLevel),
                      value: selectedServiceLevel,
                    }}
                    size="xs"
                  />
                  <RedoSingleSelectDropdownInput<RateTableValueMode>
                    className={styles.rateTableValueModeDropdown}
                    label="Value mode"
                    options={valueModeOptions.map((s) => ({
                      id: s,
                      type: "text",
                      text: getRateTableValueModeLabel(s),
                      value: s,
                    }))}
                    optionSelected={({ item }) => {
                      setSelectedValueMode(item.value);
                    }}
                    selectedItem={{
                      id: selectedValueMode,
                      type: "text",
                      text: getRateTableValueModeLabel(selectedValueMode),

                      value: selectedValueMode,
                    }}
                    size="xs"
                  />
                  <RedoSingleSelectDropdownInput<OutboundRateTableType>
                    className={styles.rateTableTypeDropdown}
                    label="Rate table type"
                    options={rateTableTypeOptions.map((s) => ({
                      id: s,
                      type: "text",
                      text:
                        s === RateTableType.OUTBOUND_WEIGHT_BY_ZONE
                          ? "Weight"
                          : "Size",

                      value: s,
                    }))}
                    optionSelected={({ item }) => {
                      setSelectedRateTableType(item.value);
                    }}
                    selectedItem={{
                      id: selectedRateTableType,
                      type: "text",
                      text:
                        selectedRateTableType ===
                        RateTableType.OUTBOUND_WEIGHT_BY_ZONE
                          ? "Weight"
                          : "Size",
                      value: selectedRateTableType,
                    }}
                    size="xs"
                  />
                  {!rateTableInDB ? (
                    <RedoButton
                      disabled={readonly || addRateTableLoad.pending}
                      hierarchy="primary"
                      onClick={() => triggerAddRateTable()}
                      pending={addRateTableLoad.pending}
                      size="sm"
                      text="Create"
                      theme="normal"
                    />
                  ) : (
                    rateTableDataInput.changed && (
                      <RedoButton
                        disabled={saveDisabled}
                        hierarchy="primary"
                        onClick={saveRateCard}
                        pending={savePending}
                        size="sm"
                        text="Save table"
                        theme="normal"
                      />
                    )
                  )}
                </Flex>
                {rateTableInDB && (
                  <Flex align="flex-end" dir="row">
                    <RedoButton
                      disabled={readonly}
                      hierarchy="tertiary"
                      IconLeading={TrashIcon}
                      onClick={deleteRateTable}
                      size="sm"
                      text="Delete"
                      theme="destructive"
                    />
                    <RedoButton
                      hierarchy="tertiary"
                      IconLeading={CopyIcon}
                      onClick={copyTable}
                      size="sm"
                      text="Copy table"
                      theme="normal"
                    />
                    <RedoButton
                      disabled={readonly}
                      hierarchy="tertiary"
                      IconLeading={PlusIcon}
                      onClick={addRightColumn}
                      size="sm"
                      text="Add column"
                      theme="normal"
                    />
                    <RedoButton
                      disabled={readonly}
                      hierarchy="tertiary"
                      IconLeading={PlusIcon}
                      onClick={addBottomRow}
                      size="sm"
                      text="Add row"
                      theme="normal"
                    />
                  </Flex>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        {rateTableInDB && (
          <Flex dir="column">
            <Flex dir="row">
              <LabelRateTableForm
                deleteColumn={deleteColumn}
                deleteRow={deleteRow}
                input={rateTableDataInput}
                readonly={readonly}
                xAxisLabel="Zone"
                yAxisLabel={
                  selectedRateTableType ===
                  RateTableType.OUTBOUND_WEIGHT_BY_ZONE
                    ? "Max LB"
                    : "Max Cubic feet"
                }
              />
            </Flex>
            <Flex dir="column" gap="md" pt="md" w="xs">
              {selectedValueMode !== RateTableValueMode.SpreadPercent && (
                <RedoCheckbox
                  disabled={readonly}
                  label="Use percentage-based default"
                  setValue={(value) =>
                    rateTableDataInput.inputs.useDefaultValuePercentage.setValue(
                      value === true ? true : undefined,
                    )
                  }
                  value={
                    rateTableDataInput.inputs.useDefaultValuePercentage.value ??
                    false
                  }
                />
              )}
              {selectedValueMode !== RateTableValueMode.SpreadPercent &&
              rateTableDataInput.inputs.useDefaultValuePercentage.value ? (
                <FormTextInput
                  disabled={readonly}
                  input={{
                    value:
                      rateTableDataInput.inputs.defaultValuePercentage.value?.toString() ??
                      "",
                    setValue: (value: string) => {
                      const numValue = value === "" ? undefined : Number(value);
                      rateTableDataInput.inputs.defaultValuePercentage.setValue(
                        numValue,
                      );
                    },
                    errors:
                      rateTableDataInput.inputs.defaultValuePercentage.errors,
                    changed:
                      rateTableDataInput.inputs.defaultValuePercentage.changed,
                    allErrors:
                      rateTableDataInput.inputs.defaultValuePercentage
                        .allErrors,
                  }}
                  label="Default spread percentage (%)"
                  type="number"
                />
              ) : (
                <FormTextInput
                  disabled={readonly}
                  input={rateTableDataInput.inputs.defaultValue}
                  label={
                    selectedValueMode === RateTableValueMode.SpreadPercent
                      ? "Default markup (%) (used if no match is found in the table)"
                      : selectedValueMode === RateTableValueMode.Rate
                        ? "Default rate (used if no match is found in the table)"
                        : "Default spread (used if no match is found in the table)"
                  }
                  type="number"
                />
              )}
            </Flex>
          </Flex>
        )}
      </Card>
    );
  },
);
